// 이정표 카드 모델(순수 로직, DOM 없음). 데이터는 shared/content/quests.ts MILESTONES(3개월 C3). 퀘스트 창 「이정표」 줄과 단위 시험이 쓴다.
// 이정표는 이미 있는 일(스테이지 첫 클리어·레벨·하드 보스·전투방 정리)을 '다음 목표' 한 줄로 보이게 할 뿐 보상·판정을 새로 만들지 않는다.
import { MILESTONES, type MilestoneDef } from '../shared/content/quests';
import { hardOrdinal, hellOrdinal, parseHardStage, parseHellStage, parseStage, stageLabel, stageOrdinal } from '../shared/content/stages';
import type { AnyStageId } from '../shared/content/types';
import type { RegionId } from '../shared/levels';
import type { QuestAction } from './villageQuestModel';

export interface MilestoneCtx {
  /** 영웅 레벨 */
  readonly level: number;
  /** 스테이지 첫 클리어 횟수(일반 rN-sNN, 하드 hN-sNN). 모르면 0 */
  readonly clears: (stageId: string) => number;
  /** 지금 던전 한 판의 전투방 정리 수. 던전이 아니면 null */
  readonly run?: { readonly stage: string; readonly rooms: number } | null;
}

export interface MilestoneStatus { readonly done: boolean; readonly cur: number; readonly max: number }

/**
 * 스테이지 첫 클리어 횟수 함수. 옛 저장(save.v1)에서 이관한 계정은 깬 수(cleared)만 있고 스테이지별 횟수(clears)가 비어 있다 —
 * 서수가 깬 수 이하인 스테이지는 깬 것으로 센다(progress.ts claimedFirst와 같은 규칙, 하드는 하드 깬 수).
 */
export function milestoneClears(p: { readonly stageClears: (id: string) => number; readonly cleared?: number } | undefined, hard: { readonly stageClears: (id: string) => number; readonly cleared?: number } | undefined, hell?: { readonly stageClears: (id: string) => number; readonly cleared?: number }): (id: string) => number {
  return (id) => {
    if (parseHellStage(id)) return (hell?.stageClears(id) ?? 0) || (hell?.cleared !== undefined && hellOrdinal(id) <= hell.cleared ? 1 : 0);
    if (parseHardStage(id)) return (hard?.stageClears(id) ?? 0) || (hard?.cleared !== undefined && hardOrdinal(id) <= hard.cleared ? 1 : 0);
    if (!parseStage(id)) return 0;
    return (p?.stageClears(id) ?? 0) || (p?.cleared !== undefined && stageOrdinal(id) <= p.cleared ? 1 : 0);
  };
}

/** 이정표 하나의 달성·진행 수치 */
export function milestoneStatus(m: MilestoneDef, c: MilestoneCtx): MilestoneStatus {
  const g = m.goal;
  if (g.type === 'level') return { done: c.level >= g.level, cur: Math.min(c.level, g.level), max: g.level };
  if (g.type === 'clearStage') { const d = c.clears(g.stage) > 0; return { done: d, cur: d ? 1 : 0, max: 1 }; }
  // room: 그 스테이지를 이미 깼으면 방도 다 정리한 것. 아니면 지금 그 판에서 정리한 수
  if (c.clears(g.stage) > 0) return { done: true, cur: g.count, max: g.count };
  const rooms = c.run && c.run.stage === g.stage ? c.run.rooms : 0;
  return { done: rooms >= g.count, cur: Math.min(rooms, g.count), max: g.count };
}

export interface NextMilestone {
  readonly def: MilestoneDef;
  readonly status: MilestoneStatus;
  /** 이 이정표를 포함해 남은 이정표 수 */
  readonly remaining: number;
}

/** 아직 못 이룬 가장 앞(atMin 순) 이정표. 모두 이뤘으면 null */
export function nextMilestone(c: MilestoneCtx, list: readonly MilestoneDef[] = MILESTONES): NextMilestone | null {
  let remaining = 0, first: NextMilestone | null = null;
  for (const def of list) {
    const status = milestoneStatus(def, c);
    if (status.done) continue;
    remaining++;
    if (!first) first = { def, status, remaining: 0 };
  }
  return first ? { ...first, remaining } : null;
}

/** 지역 문 동작: 일반 스테이지 이정표는 그 지역 문으로 자동 이동할 수 있다(하드·레벨은 없음) */
export function milestoneAction(m: MilestoneDef): QuestAction | null {
  const g = m.goal;
  const id = g.type === 'level' ? '' : g.stage;
  const r = /^r(\d)-s\d\d$/.exec(id);
  return r && Number(r[1]) >= 1 && Number(r[1]) <= 4 ? { kind: 'door', region: ('r' + r[1]) as RegionId } : null;
}

/** 목록 둘째 줄: 진행 수치(레벨은 현재/목표, 전투방은 n/m, 클리어는 지금 도전할 곳) */
export function milestoneSub(n: NextMilestone): string {
  const g = n.def.goal;
  if (g.type === 'level') return '지금 Lv ' + n.status.cur + ' / ' + g.level;
  if (g.type === 'room') return '정리한 전투방 ' + n.status.cur + ' / ' + g.count;
  const label = stageLabel(g.stage as AnyStageId);
  return (label ? label + ' · ' : '') + '이정표 ' + n.remaining + '개 더 남음';
}
